/* istanbul ignore file */
import { GetSupplierAndAIAnswersResponse, SupplierCategoriesResponse, SupplierHashResponse, SupplierSurveyResponse } from "@/shared/services/interface/responses/suplier-response";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getSupplierHashThunk, getSupplierAndAIAnswersThunk, getSupplierCategoriesThunk } from "../thunks/productAnswersThunk";
import { ISupabaseCompareItemAnswerResponse } from "@/shared/services/interface/responses/ISupabaseCompare";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";

interface IProductAnswerState {
  supplierHashResponse: SupplierHashResponse | null;
  isLoading: boolean;
  error: string | null;
  supplierAndAIAnswersResponse: GetSupplierAndAIAnswersResponse | {
    supplierSurveys: SupplierSurveyResponse[];
    specificationItemAnswers: ISupabaseCompareItemAnswerResponse[];
  };
  // Supplier categories with survey answers
  supplierCategoriesResponse: SupplierCategoriesResponse | null;
  isCategoriesLoading: boolean;
  categoriesError: string | null;
  transientSelectedAnswer: {
    specificationItemId: string;
    status: SpecificationItemAnswerStatus;
  } | null;
}

const initialState: IProductAnswerState = {
  supplierHashResponse: null,
  supplierAndAIAnswersResponse: {
    supplierSurveys: [],
    specificationItemAnswers: [],
  },
  isLoading: false,
  error: null,
  // Supplier categories initial state
  supplierCategoriesResponse: null,
  isCategoriesLoading: false,
  categoriesError: null,
  transientSelectedAnswer: null,
};

export const productAnswerSlice = createSlice({
  name: "productAnswer",
  initialState,
  reducers: {
    addSupplierSurvey: (state, action: PayloadAction<SupplierSurveyResponse>) => {
      const existingIndex = state.supplierAndAIAnswersResponse?.supplierSurveys?.findIndex(survey => survey.specificationItemId === action.payload.specificationItemId);
      if (existingIndex === undefined || existingIndex === -1) {
        state.supplierAndAIAnswersResponse?.supplierSurveys.push(action.payload);
      } else {
        state.supplierAndAIAnswersResponse.supplierSurveys[existingIndex] = action.payload;
      }
    },
    setTransientSelectedAnswer: (
      state,
      action: PayloadAction<{
        specificationItemId: string;
        status: SpecificationItemAnswerStatus;
      } | null>
    ) => {
      state.transientSelectedAnswer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSupplierHashThunk.fulfilled, (state, action) => {
      state.supplierHashResponse = action.payload as any;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(getSupplierHashThunk.rejected, (state, action) => {
      state.supplierHashResponse = null;
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getSupplierHashThunk.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getSupplierAndAIAnswersThunk.fulfilled, (state, action) => {
      state.supplierAndAIAnswersResponse = action.payload.data as any;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(getSupplierAndAIAnswersThunk.rejected, (state, action) => {
      state.supplierAndAIAnswersResponse = {
        supplierSurveys: [],
        specificationItemAnswers: [],
      };
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getSupplierAndAIAnswersThunk.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    // Supplier Categories Thunk
    builder.addCase(getSupplierCategoriesThunk.fulfilled, (state, action) => {
      state.supplierCategoriesResponse = action.payload.data as SupplierCategoriesResponse;
      state.isCategoriesLoading = false;
      state.categoriesError = null;
    });
    builder.addCase(getSupplierCategoriesThunk.rejected, (state, action) => {
      state.supplierCategoriesResponse = null;
      state.isCategoriesLoading = false;
      state.categoriesError = action.payload as string;
    });
    builder.addCase(getSupplierCategoriesThunk.pending, (state) => {
      state.isCategoriesLoading = true;
      state.categoriesError = null;
    });
  },
});

export const { addSupplierSurvey, setTransientSelectedAnswer } = productAnswerSlice.actions;
export default productAnswerSlice.reducer;
